/**
 * The game page's browser floor (ADR-019's amendment of 2026-10-04, CLIENT.md 7.37): Safari 15.5, Chrome 120 and
 * Firefox 112, the oldest of each that has everything the page cannot run without. A plain script in the page
 * (`index.html`) checks the features below before the page's module runs and leaves what is missing on
 * `window.otBrowserLacks`; with anything missing it shows the sentence `floor.unsupported` of the language files, which `bun run lang:extract` writes into the page, in place of the game, and the module
 * does not start, so an old browser is told it is old and never that the server is offline.
 */

/**
 * One feature the page's check requires: the name the check reports it by, what the page needs it for, and the
 * first Safari, Chrome and Firefox to have it.
 */
export interface FloorFeature {
  readonly name: string;
  readonly need: string;
  readonly since: string;
}

/**
 * Every feature the check requires, in the order it checks them. The floor is the highest first version in each
 * browser's column: Safari 15.5 and Firefox 112 by `inert`, Chrome 120 by `mask-image`.
 */
export const FLOOR_FEATURES: readonly FloorFeature[] = [
  { name: "modules", need: "the page's script is one ES module", since: "Safari 11, Chrome 61, Firefox 60" },
  { name: "syntax", need: "the bundle is not transpiled: class fields, optional chaining, nullish coalescing and logical assignment", since: "Safari 14.1, Chrome 85, Firefox 79" },
  { name: "Array.prototype.at", need: "called across the page, the shared rules and the renderer", since: "Safari 15.4, Chrome 92, Firefox 90" },
  { name: "Object.hasOwn", need: "the protocol's and the rules' checks", since: "Safari 15.4, Chrome 93, Firefox 92" },
  { name: "fetch", need: "every request, each with an AbortController's signal", since: "Safari 12.1, Chrome 66, Firefox 57" },
  { name: "ResizeObserver", need: "the game's view follows its window", since: "Safari 13.1, Chrome 64, Firefox 69" },
  { name: "inert", need: "the HUD, the chat and the reading panel take no focus under an entry screen", since: "Safari 15.5, Chrome 102, Firefox 112" },
  { name: "mask-image", need: "every glyph of the kit is an unprefixed CSS mask over the UI atlas", since: "Safari 15.4, Chrome 120, Firefox 53" },
  { name: "WebGL", need: "Phaser 4.2.1 draws on a WebGL 1 context (WebGL 2 is not needed); `webgl.ts` then checks one can be made", since: "Safari 8, Chrome 9, Firefox 4" },
];

/**
 * What the page's check found missing.
 *
 * @param page The window the check wrote to.
 * @returns The names of the missing features; empty when the browser has them all, or when the check did not run.
 */
export function browserLacks(page: { readonly otBrowserLacks?: unknown } = window as { readonly otBrowserLacks?: unknown }): readonly string[] {
  const lacks = page.otBrowserLacks;
  return Array.isArray(lacks) ? lacks.filter((name): name is string => typeof name === "string") : [];
}
